import React from 'react';
import { useWatch } from 'react-hook-form';
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
} from 'react-hook-form';
import { VirtualizedFormSelect } from '@roger/ui-components';

import { KINNSER_ADMISSION_SOURCE_OPTIONS } from '../../hooks/useCreatePatientForm';
import {
  ADMISSION_SOURCE_FIELD,
  resolveUnsupportedAdmissionSource,
} from './kinnserAdmissionSource';
import type {
  AdmissionSourceIntakeContext,
  KinnserAdmissionSourceFormFields,
} from './kinnserAdmissionSource';

/**
 * The "Admission Source" select, shared by the two push surfaces that can set
 * an admission source in WellSky:
 *
 * - the new-patient create-episode form, where the code rides the
 *   create/onboard payload onto the patient form's `ListPatientSourceKey`, and
 * - the scheduling tab for an existing patient whose creation type is
 *   `new_admission`, where the backend reads the intake column and
 *   forwards it on the auto-add admission call, landing on the "Add new
 *   episode" form's `admission-source` select.
 *
 * Both write the same intake column through the same autosave, so the option
 * list, the prefill mirror and the unsendable-value notice live here once.
 *
 * What differs is what an *unset* field means downstream, which is why
 * `unsetOutcome` is required rather than defaulted: on the new-patient path
 * nothing is sent and WellSky applies its own default; on the existing-patient
 * path the key is omitted and WellSky keeps the source carried forward from
 * the prior admission. Getting that sentence wrong tells a coordinator the
 * opposite of what the push will do.
 *
 * The "left unset" gloss is deliberately NOT rendered here. On the scheduling
 * tab this field shares a row with two referrals that carry the identical
 * rule, and a line per field made a coordinator read it three times. The row
 * owns one line instead -- see `KinnserReferralSourceFields` -- fed by
 * `useAdmissionSourceRowHintState` in ./kinnserAdmissionSource.
 */

interface KinnserAdmissionSourceFieldProps<T extends FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
  /**
   * Persist a field immediately, without waiting for a blur.
   *
   * Required, not optional: this is the only clearable control in the row, and
   * a call site that forgot to wire it would reintroduce the silent
   * clear-then-reload data loss below. A type error is the cheapest place to
   * catch that.
   */
  saveToServer: (data: Partial<T>) => void;
  disabled?: boolean;
  selectedIntake: AdmissionSourceIntakeContext | null | undefined;
  /**
   * What WellSky does when this field is left unset. Selects the wording of
   * the unsendable-value notice.
   *
   * - `wellsky_default` — new patient: nothing is sent, WellSky defaults.
   * - `carried_forward` — existing patient: the key is omitted from the
   *   auto-add request, so the new admission inherits the prior admission's
   *   source.
   */
  unsetOutcome: 'wellsky_default' | 'carried_forward';
}

export function KinnserAdmissionSourceField<
  T extends FieldValues & KinnserAdmissionSourceFormFields,
>({
  control,
  register,
  errors,
  handleFieldBlur,
  saveToServer,
  disabled = false,
  selectedIntake,
  unsetOutcome,
}: KinnserAdmissionSourceFieldProps<T>) {
  // The generic constraint guarantees the key exists on T, but TypeScript
  // cannot evaluate `Path<T>` for an unresolved T, so it is narrowed once here.
  const admissionSourceName = ADMISSION_SOURCE_FIELD as Path<T>;
  const admissionSourceErrors =
    errors as FieldErrors<KinnserAdmissionSourceFormFields>;

  const admissionSourceValue =
    (useWatch({ control, name: admissionSourceName }) as
      string | null | undefined) ?? '';
  const { label: unsupportedAdmissionSourceLabel, fromOverride } =
    resolveUnsupportedAdmissionSource(selectedIntake, admissionSourceValue);

  const admissionSourceField = register(admissionSourceName, {
    onBlur: handleFieldBlur,
  });

  // Persist on the change itself, not on blur. react-select's clear control
  // empties the value without moving focus off the control, so a blur-only
  // save never fires: the UI empties, nothing is sent, and a reload brings the
  // old value back -- the coordinator's clear is silently lost, and until they
  // reload the screen and the server disagree. (QA reproduced exactly
  // that: after the clear, `document.activeElement` was still
  // `.react-select__input` and `GET /intake/{id}` still returned the old code.)
  // The referral pair beside this one has guarded the identical hazard since
  // Saving here also removes the race where a user clears and
  // immediately pushes with a deferred blur PUT still in flight.
  //
  // `|| ''` matters: an empty string is the deliberate clear, and it is what
  // suppresses the server-side fallback to the intake's captured
  // discharge_facility_type. Sending null or omitting it would read as
  // "untouched" and the fallback would put the value straight back.
  const handleAdmissionSourceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    void admissionSourceField.onChange(event);
    saveToServer({
      [ADMISSION_SOURCE_FIELD]: event.target.value || '',
    } as Partial<T>);
  };

  const unsetSentence =
    unsetOutcome === 'carried_forward'
      ? 'The new admission keeps the admission source carried forward from the prior admission unless you pick one above.'
      : "WellSky's default will be used unless you pick one of the sources above.";

  return (
    <div>
      <VirtualizedFormSelect
        label='Admission Source'
        placeholder='Select source...'
        options={KINNSER_ADMISSION_SOURCE_OPTIONS}
        // Clearable on both surfaces, unlike the referral pair beside it on the
        // scheduling tab. A referral has no intake fallback, so "clear" there
        // would promise a removal WellSky never performs. Admission source does
        // have one -- the intake's captured discharge_facility_type, which both
        // the prefill and the server fall back to -- so clearing is the only
        // way a coordinator can say "do not send the extracted value", and on
        // the existing-patient path that is what keeps the prior admission's
        // source.
        isClearable
        // The row this sits in is always three columns, which puts each control
        // near 110px; see `KinnserReferralSourceFields` for the geometry.
        compactIndicators
        disabled={disabled}
        error={admissionSourceErrors.kinnser_admission_source?.message}
        {...admissionSourceField}
        onChange={handleAdmissionSourceChange}
      />
      {/* break-words: the captured value can be arbitrary free text
          from intake, so it must wrap rather than widen the row. */}
      {unsupportedAdmissionSourceLabel && (
        <p
          role='status'
          className='mt-1 break-words text-xs text-amber-700'
          data-testid='admission-source-unsupported-value'
        >
          {`${
            fromOverride
              ? `A saved admission source of "${unsupportedAdmissionSourceLabel}"`
              : `Intake captured "${unsupportedAdmissionSourceLabel}"`
          }, which WellSky has no matching option for. ${unsetSentence}`}
        </p>
      )}
    </div>
  );
}
